import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { Draggable } from "react-beautiful-dnd";

import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import { getType, isConditionalQuestion, types } from "./itemTypes";
import ChangeChoiceButton from "./ChangeChoiceButton";
import Open from "./Open";
import Choice from "./Choice";
import Slider from "./Slider";
import InputField from "./Components/InputField";
import ConditionalMessage from "./Components/ConditionalMessage";
import AddButton from "./AddButton";
import LabelAutocomplete from "./Components/LabelAutocomplete";
import { choiceQuestions } from "./itemTypesAddButton";
import { ExpandButton } from "../Components/ExpandButton";
import ConditionalDialog from "./Components/ConditionalDialog";

const buttonSize = "small";

// Return UI for particular question type
function QuestionType(props) {
  switch (getType(props.question.type)) {
    case types.unknown:
    case types.container:
    case types.open:
      return <Open {...props} />;
    case types.choice:
      return <Choice {...props} />;
    case types.slider:
      return <Slider {...props} />;
    default:
      return <div>Unknown question</div>;
  }
}

function Question({
  index,
  questionLeaf,
  position,
  indentLevel,
  selected,
  isExpanded,
  previousQuestion,
  parentQuestion,
  handleQuestionTreeApi,
  disabled = false,
}) {
  const { t } = useTranslation();
  const didMount = useRef(false);
  const [openOptionDialog, setOpenOptionDialog] = useState(false);
  const [conditionalDialog, setConditionalDialog] = useState({
    open: false,
    mode: "",
  });
  const initQuestion = { ...questionLeaf.value, items: [] };
  const [question, setQuestion] = useState(initQuestion);
  const selectedId = selected.split("#")?.[1] ?? "";

  const indentMargin = (indentLevel.split(".").length - 2) * 4;
  const isConditional = position.split(".").length - 1 > 1;
  const isParent = questionLeaf.children.length > 0;

  const isConditionalAllowed = isConditionalQuestion(
    previousQuestion?.value?.type
  );

  useEffect(() => {
    if (didMount.current) {
      const timeOutId = setTimeout(() => {
        handleQuestionTreeApi({
          type: "UPDATE_QUESTION",
          payload: {
            key: questionLeaf.key,
            value: question,
          },
        });
      }, 200);
      return () => {
        clearTimeout(timeOutId);
      };
    } else {
      didMount.current = true;
    }
  }, [question]);

  // Card area is pressed
  const onMouseDownHandler = (optionId) => {
    handleQuestionTreeApi({
      type: "SELECT_QUESTION",
      payload:
        typeof optionId === "string"
          ? `${questionLeaf.key}#${optionId}`
          : questionLeaf.key,
    });
  };

  const updateHandler = (data) => {
    setQuestion((prev) => ({ ...prev, ...data }));
  };

  const duplicateHandler = () =>
    handleQuestionTreeApi({
      type: "DUPLICATE_QUESTION",
      payload: { key: questionLeaf.key, value: question },
    });

  const addHandler = (questionType) =>
    handleQuestionTreeApi({
      type: "INSERT_QUESTION_AFTER",
      payload: { key: questionLeaf.key, value: { type: questionType } },
    });

  // conditional dialog
  const handleConditionalDialogOpen = (mode) => {
    // don't open the conditional dialog for container items
    if (previousQuestion?.value?.type === "container") {
      conditionalHandler({
        conditions: question.conditions,
        defaultValue: null,
      });
    } else {
      setConditionalDialog({ open: true, mode });
    }
  };
  const handleConditionalDialogClose = () =>
    setConditionalDialog({ open: false, mode: "" });
  // make the question conditional
  const conditionalHandler = ({ conditions, defaultValue }) => {
    handleQuestionTreeApi({
      type: "MAKE_CONDITIONAL",
      payload: {
        key: questionLeaf.key,
        toParentKey: previousQuestion.key,
        index: previousQuestion.children.length,
        value: conditions,
        defaultValue: defaultValue,
      },
    });
    handleConditionalDialogClose();
  };
  // update the conditions of the question
  const conditionalUpdateHandler = (props) => {
    updateHandler(props);
    handleConditionalDialogClose();
  };
  // make the question unconditional
  const unConditionalHandler = () => {
    const arr = position.split(".");
    const index = arr?.[arr.length - 3] ?? 1;
    handleQuestionTreeApi({
      type: "MAKE_UNCONDITIONAL",
      payload: {
        key: questionLeaf.key,
        toParentKey: parentQuestion.parentKey,
        index,
      },
    });
  };

  // change the question type
  const changeType = (questionType) => {
    updateHandler({ type: questionType });
  };

  return (
    <Draggable
      draggableId={question.id + ""}
      index={index}
      isDragDisabled={disabled}
    >
      {(provided) => (
        <Box
          ref={provided.innerRef}
          {...provided.draggableProps}
          id={question.id}
        >
          <Box paddingBottom={2} paddingLeft={indentMargin}>
            <Card
              sx={{
                display: "flex",
                background: "rgb(248 248 248)",
                borderBottom:
                  isParent && !isExpanded
                    ? "7px solid rgba(0,0,0,0.12)"
                    : "1px solid rgba(0,0,0,0.12)",
              }}
              variant="outlined"
            >
              <Box
                display="flex"
                flexDirection="column"
                justifyContent={"space-between"}
                width="15%"
              >
                <Box>
                  <Box
                    {...provided.dragHandleProps}
                    onMouseDown={onMouseDownHandler}
                    onFocus={onMouseDownHandler}
                    sx={{
                      display: "flex",
                      paddingTop: 2,
                      paddingLeft: 1,
                      paddingRight: 1,
                    }}
                  >
                    {!disabled ? (
                      <DragIndicatorIcon
                        fontSize="small"
                        sx={{
                          display: "flex",
                          transform: "rotate(90deg)",
                          marginLeft: 1,
                          color: "text.secondary",
                        }}
                      />
                    ) : null}
                    <Typography
                      variant="subtitle2"
                      sx={{
                        paddingLeft: 1,
                        paddingRight: 1,
                        color: "text.secondary",
                      }}
                    >
                      {position}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      paddingLeft: 1,
                      paddingRight: 1,
                    }}
                    onMouseDown={onMouseDownHandler}
                    onFocus={onMouseDownHandler}
                  >
                    <LabelAutocomplete
                      question={question.title}
                      inputValue={question.label}
                      placeholder={t("editor.placeholders.label")}
                      handleUpdateLabel={(label) => updateHandler({ label })}
                      error={false}
                      isLabelSelected={selectedId === "label"}
                      isSelected={true}
                      disabled={disabled}
                    />
                  </Box>
                </Box>
                <Box paddingLeft={1} paddingBottom={1}>
                  <ExpandButton
                    expand={isExpanded}
                    onClick={() =>
                      handleQuestionTreeApi({
                        type: "TOGGLE_EXPAND_QUESTION",
                        payload: {
                          key: questionLeaf.key,
                        },
                      })
                    }
                    aria-expanded={isExpanded}
                    aria-label="show conditional questions"
                    size="small"
                    fontSize="small"
                    sx={{ display: isParent ? "flex" : "none" }}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  borderRight: "1px solid rgba(0, 0, 0, 0.2)",
                  marginTop: 2,
                  marginBottom: 2,
                }}
              />
              <Box
                display="block"
                width="100%"
                onMouseDown={onMouseDownHandler}
                onFocus={onMouseDownHandler}
              >
                <Box display="block" padding={1} width={"100%"}>
                  <Box display="flex">
                    <InputField
                      type="question"
                      inputValue={question.title}
                      placeholder={
                        getType(question.type) !== types.unknown
                          ? t(
                              `editor.questionTypes.${question.type}.placeholder`
                            )
                          : `${question.type} ${t(`editor.placeholders.item`)}`
                      }
                      onChange={(e) => updateHandler({ title: e.target.value })}
                      autoFocus={selectedId === "question"}
                      disabled={disabled}
                    />

                    <Box display={"flex"} alignSelf={"flex-start"}>
                      <Tooltip title={t("editor.buttons.unConditional")}>
                        <span>
                          <IconButton
                            aria-label="unconditional"
                            size={buttonSize}
                            sx={{
                              padding: 1,
                              display: isConditional ? "flex" : "none",
                            }}
                            onClick={unConditionalHandler}
                            disabled={disabled}
                          >
                            <SubdirectoryArrowRightIcon
                              sx={{ transform: "scaleX(-1)" }}
                              fontSize="small"
                            />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip title={t("editor.buttons.conditional")}>
                        <span>
                          <IconButton
                            aria-label="conditional"
                            size={buttonSize}
                            sx={{
                              padding: 1,
                              display: isConditionalAllowed ? "flex" : "none",
                            }}
                            onClick={() => handleConditionalDialogOpen("new")}
                            disabled={disabled}
                          >
                            <SubdirectoryArrowRightIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t("editor.buttons.duplicate")}>
                        <span>
                          <IconButton
                            aria-label="duplicate"
                            size={buttonSize}
                            sx={{ padding: 1 }}
                            onClick={(questionType) =>
                              duplicateHandler(questionType)
                            }
                            disabled={disabled}
                          >
                            <FileCopyOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t("common.buttons.remove")}>
                        <span>
                          <IconButton
                            aria-label="delete"
                            size={buttonSize}
                            sx={{ padding: 1 }}
                            onClick={() =>
                              handleQuestionTreeApi({
                                type: "REMOVE_QUESTION",
                                payload: questionLeaf.key,
                              })
                            }
                            disabled={disabled}
                          >
                            <DeleteOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <div
                        style={{
                          alignSelf: "flex-start",
                          display:
                            question.type === "multipleChoice" ||
                            question.type === "singleChoice"
                              ? "flex"
                              : "none",
                        }}
                      >
                        <Tooltip title={t("common.buttons.more")}>
                          <span>
                            <IconButton
                              aria-label="options"
                              sx={{ padding: 1 }}
                              size={buttonSize}
                              onClick={() => setOpenOptionDialog(true)}
                              disabled={disabled}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </div>
                    </Box>
                  </Box>
                  <ConditionalMessage
                    conditions={question.conditions}
                    parentQuestion={parentQuestion?.value}
                    isConditional={isConditional}
                    handleConditionalDialogOpen={() =>
                      handleConditionalDialogOpen("update")
                    }
                  />
                  {conditionalDialog.open ? (
                    <ConditionalDialog
                      open={conditionalDialog.open}
                      onClose={handleConditionalDialogClose}
                      handleSave={
                        conditionalDialog.mode === "new"
                          ? conditionalHandler
                          : conditionalUpdateHandler
                      }
                      parentQuestion={
                        conditionalDialog.mode === "new"
                          ? previousQuestion.value
                          : parentQuestion.value
                      }
                      childQuestion={question}
                    />
                  ) : null}
                  <QuestionType
                    question={question}
                    childrenLength={questionLeaf.children.length}
                    updateHandler={updateHandler}
                    isSelected={true}
                    selectedId={selectedId}
                    onMouseDown={onMouseDownHandler}
                    selected={selected}
                    open={openOptionDialog}
                    handleClose={() => setOpenOptionDialog(false)}
                    disabled={disabled}
                  />
                  {choiceQuestions.some(
                    (choiceQuestion) => choiceQuestion.type === question.type
                  ) ? (
                    <ChangeChoiceButton
                      /*                      allowMultipleChoice={question.allowMultipleChoice}
                      handleAllowMultipleChoice={(event) =>
                        updateHandler({
                          allowMultipleChoice: event.target.checked,
                        })
                      } */
                      addHandler={changeType}
                      selected={question.type}
                      disabled={disabled}
                    />
                  ) : null}
                </Box>
              </Box>
            </Card>
            {/** Show add button if the questionnaire is not curated, disabled and the parentQuestion is a known type */}
            {!disabled &&
            (isConditional
              ? [types.container, types.choice, types.slider].includes(
                  getType(parentQuestion?.value?.type)
                )
              : true) ? (
              <Box width="fit-content" margin="auto" display="flex">
                <AddButton
                  buttonId="icon-button"
                  addHandler={(questionType) => addHandler(questionType)}
                  handleQuestionTreeApi={handleQuestionTreeApi}
                  question={question}
                />
              </Box>
            ) : (
              <Box padding={1} />
            )}
          </Box>
        </Box>
      )}
    </Draggable>
  );
}

/* function questionsAreEqual(prevQuestions, nextQuestions) {
  return (
    JSON.stringify({ ...prevQuestions.questionLeaf, items: [] }) ===
      JSON.stringify({ ...nextQuestions.questionLeaf, items: [] }) &&
    prevQuestions.position === nextQuestions.position &&
    prevQuestions.isSelected === nextQuestions.isSelected &&
    prevQuestions.isExpanded === nextQuestions.isExpanded &&
    JSON.stringify(prevQuestions.question) ===
      JSON.stringify(nextQuestions.question) &&
    prevQuestions.disabled === nextQuestions.disabled
  );
} */

//export default React.memo(Question, questionsAreEqual);
export default Question;
